import React, { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import DownloadIcon from '@mui/icons-material/Download';
import IosShareIcon from '@mui/icons-material/IosShare';
import { venueImageUrl } from '../../../api';
import { trackEvent } from '../../../analytics';
import { formatInviteTime, formatWeekdayNight } from '../utils';
import { isNativeApp, saveFile } from '../../../native';
import { InvitePreviewData } from './InviteParts';

const W = 1080;
const H = 1920;
const GOLD = '#F5C542';

function loadImage(src: string, crossOrigin = true): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous'; // venue photos come from the API origin
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function drawCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement, x: number, y: number, w: number, h: number) {
  const scale = Math.max(w / img.width, h / img.height);
  const sw = w / scale;
  const sh = h / scale;
  ctx.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, x, y, w, h);
}

function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, startSize: number, font: (size: number) => string): number {
  let size = startSize;
  ctx.font = font(size);
  while (ctx.measureText(text).width > maxWidth && size > 40) {
    size -= 4;
    ctx.font = font(size);
  }
  return size;
}

// Draws the 1080×1920 story image: branding, venue photo, when/where, inviter, QR code and link.
export async function renderStory(invite: InvitePreviewData, url: string, qrCanvas: HTMLCanvasElement | null): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#030912';
  ctx.fillRect(0, 0, W, H);

  // A distinct URL for the canvas copy: the page's normal <img> may already have cached this photo
  // without CORS headers, and a canvas can't read that cached copy (it would come back tainted).
  const photoSrc = venueImageUrl(invite.venue.imageUrl);
  const photo = photoSrc ? await loadImage(`${photoSrc}${photoSrc.includes('?') ? '&' : '?'}canvas=1`) : null;
  if (photo) {
    drawCover(ctx, photo, 0, 0, W, 1150);
  } else {
    const g = ctx.createRadialGradient(760, 380, 40, 760, 380, 700);
    g.addColorStop(0, '#b91c87');
    g.addColorStop(1, '#101a3f');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, 1150);
  }
  const fade = ctx.createLinearGradient(0, 300, 0, 1200);
  fade.addColorStop(0, 'rgba(3,9,18,0.15)');
  fade.addColorStop(1, 'rgba(3,9,18,1)');
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, W, 1200);
  const topShade = ctx.createLinearGradient(0, 0, 0, 320);
  topShade.addColorStop(0, 'rgba(3,9,18,0.85)');
  topShade.addColorStop(1, 'rgba(3,9,18,0)');
  ctx.fillStyle = topShade;
  ctx.fillRect(0, 0, W, 320);

  const logo = await loadImage('/logo2.png', false);
  if (logo) {
    const lw = 560;
    ctx.drawImage(logo, (W - lw) / 2, 90, lw, (logo.height / logo.width) * lw);
  }
  // The logo image already includes the "Royal People Royal Choice" tagline.
  ctx.textAlign = 'center';

  let y = 980;
  ctx.fillStyle = GOLD;
  ctx.font = 'bold 64px Arial';
  ctx.fillText("YOU'RE INVITED 🎉", W / 2, y);
  y += 90;
  ctx.fillStyle = '#ffffff';
  ctx.font = '600 52px Arial';
  ctx.fillText(formatWeekdayNight(invite.inviteDateTime).toUpperCase(), W / 2, y);
  y += 120;
  const name = invite.venue.name.toUpperCase();
  fitText(ctx, name, W - 120, 118, (s) => `bold ${s}px Georgia`);
  ctx.fillText(name, W / 2, y);
  y += 110;
  ctx.fillStyle = GOLD;
  ctx.font = 'bold 84px Arial';
  ctx.fillText(formatInviteTime(invite.inviteDateTime), W / 2, y);
  y += 80;
  ctx.fillStyle = '#ffffff';
  ctx.font = '600 44px Arial';
  ctx.fillText([invite.venue.area, invite.venue.city].filter(Boolean).join(' · ').toUpperCase(), W / 2, y);
  y += 72;
  ctx.fillStyle = '#b8c0cc';
  ctx.font = '40px Arial';
  ctx.fillText(`Invited by ${invite.host.displayName}`, W / 2, y);

  // QR code on a white rounded tile.
  const qrSize = 300;
  const tile = qrSize + 40;
  const qx = (W - tile) / 2;
  const qy = y + 50;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.roundRect(qx, qy, tile, tile, 24);
  ctx.fill();
  if (qrCanvas) ctx.drawImage(qrCanvas, qx + 20, qy + 20, qrSize, qrSize);

  const py = qy + tile + 50;
  ctx.fillStyle = GOLD;
  ctx.beginPath();
  ctx.roundRect(W / 2 - 300, py, 600, 96, 48);
  ctx.fill();
  ctx.fillStyle = '#111111';
  ctx.font = 'bold 42px Arial';
  ctx.fillText('Join on Cheers2Night', W / 2, py + 62);
  ctx.fillStyle = '#b8c0cc';
  ctx.font = '32px Arial';
  ctx.fillText(url.replace(/^https?:\/\//, ''), W / 2, py + 150);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}

// "Create Instagram Story": builds the image, previews it, then shares it through the device's
// share sheet (Instagram appears there on phones) or saves it for manual posting.
export function InstagramStoryCard({ invite, url }: { invite: InvitePreviewData; url: string }) {
  const qrWrap = useRef<HTMLDivElement>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Wait a tick so the hidden QR canvas has painted.
    const t = window.setTimeout(async () => {
      const b = await renderStory(invite, url, qrWrap.current?.querySelector('canvas') ?? null);
      if (cancelled) return;
      if (!b) { setFailed(true); return; }
      setBlob(b);
      setPreview(URL.createObjectURL(b));
    }, 50);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [invite, url]);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const file = blob ? new File([blob], 'cheers2night-invite.png', { type: 'image/png' }) : null;
  // The app shares through Android's share sheet; the website only where the browser can share files.
  const canShareFile = !!file && (isNativeApp || (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })));

  const share = async () => {
    if (!file) return;
    trackEvent('hangout_invite_shared_instagram');
    if (isNativeApp) {
      await saveFile(file.name, file, `Night out at ${invite.venue.name}`);
      return;
    }
    try {
      await navigator.share({ files: [file], title: `Night out at ${invite.venue.name}`, text: url });
    } catch {
      /* user closed the share sheet */
    }
  };

  const save = () => {
    if (!preview) return;
    trackEvent('hangout_invite_shared_instagram', { method: 'download' });
    const a = document.createElement('a');
    a.href = preview;
    a.download = 'cheers2night-invite.png';
    a.click();
  };

  return (
    <div className="hg-story-preview">
      <div ref={qrWrap} style={{ position: 'absolute', left: -9999, top: 0 }} aria-hidden="true">
        <QRCodeCanvas value={url} size={600} marginSize={1} level="M" />
      </div>
      {failed && <div className="hg-notice error">Couldn't create the story image on this device.</div>}
      {!preview && !failed && <p className="hg-muted">Creating your story…</p>}
      {preview && (
        <>
          <img src={preview} alt="Instagram story preview of your invite" />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 10, flexWrap: 'wrap' }}>
            {canShareFile && <button type="button" className="hg-btn hg-gold hg-sm" onClick={share}><IosShareIcon />Share to Story</button>}
            <button type="button" className="hg-btn hg-outline hg-sm" onClick={save}><DownloadIcon />Save image</button>
          </div>
          <p className="hg-muted" style={{ fontSize: 12, marginTop: 8 }}>
            {canShareFile ? 'Pick Instagram in the share menu, then "Story".' : 'Save the image, then add it to your Instagram story.'}
          </p>
        </>
      )}
    </div>
  );
}
